// A short silent WAV lets the user's microphone click start the same media
// element that will later play the persona's response.
const SILENT_AUDIO = 'data:audio/wav;base64,UklGRiUAAABXQVZFZm10IBAAAAABAAEARKwAAESsAAABAAgAZGF0YQEAAACA';

export default class AudioResponsePlayer {
  constructor({ createAudio = () => new Audio(), onPlayingChange, onBlockedChange, onError }) {
    this.createAudio = createAudio;
    this.onPlayingChange = onPlayingChange;
    this.onBlockedChange = onBlockedChange;
    this.onError = onError;
    this.audio = null;
    this.queue = [];
    this.busy = false;
    this.blocked = false;
    this.disposed = false;
    this.attempt = 0;
    this.playbackRate = 1;
  }

  getAudio() {
    if (!this.audio) {
      this.audio = this.createAudio();
      this.audio.preload = 'auto';
    }
    return this.audio;
  }

  unlock() {
    if (this.disposed || this.busy || this.queue.length) return;
    const audio = this.getAudio();
    const attempt = ++this.attempt;
    audio.src = SILENT_AUDIO;
    // Call play synchronously inside the click, before permissions or fetch.
    try {
      Promise.resolve(audio.play()).then(() => {
        if (!this.disposed && attempt === this.attempt) audio.pause();
      }).catch(() => {
        // A browser may still require a click when the real reply arrives.
        // playNext preserves that reply and exposes the retry control.
      });
    } catch (error) {
      // Playback restrictions are handled when there is a response to play.
    }
  }

  enqueue(base64Audio) {
    if (!this.disposed) this.queue.push(`data:audio/mp3;base64,${base64Audio}`);
  }

  playNext() {
    if (this.disposed || this.busy || this.blocked || !this.queue.length) return;
    const audio = this.getAudio();
    const attempt = ++this.attempt;
    this.busy = true;
    audio.src = this.queue[0];
    audio.playbackRate = this.playbackRate;

    const isCurrent = () => !this.disposed && attempt === this.attempt;
    const fail = (error) => {
      if (!isCurrent() || !this.busy) return;
      // Invalidate a late play promise or duplicate media error event.
      this.attempt += 1;
      this.busy = false;
      this.onPlayingChange(false);
      if (error && error.name === 'NotAllowedError') {
        this.blocked = true;
        this.onBlockedChange(true);
      } else {
        this.queue.shift();
      }
      this.onError(error);
      if (!this.blocked) this.playNext();
    };

    audio.onended = () => {
      if (!isCurrent()) return;
      this.attempt += 1;
      this.queue.shift();
      this.busy = false;
      this.onPlayingChange(false);
      this.playNext();
    };
    audio.onerror = () => fail(audio.error || new Error('Audio playback failed.'));

    try {
      Promise.resolve(audio.play()).then(() => {
        if (isCurrent() && this.busy) this.onPlayingChange(true);
      }).catch(fail);
    } catch (error) {
      fail(error);
    }
  }

  resume() {
    this.blocked = false;
    this.onBlockedChange(false);
    this.playNext();
  }

  setRate(rate) {
    this.playbackRate = rate;
    if (this.audio) this.audio.playbackRate = rate;
  }

  dispose() {
    this.disposed = true;
    this.attempt += 1;
    this.queue = [];
    this.busy = false;
    if (this.audio) {
      this.audio.onended = null;
      this.audio.onerror = null;
      this.audio.pause();
      this.audio.removeAttribute('src');
    }
  }
}
